import React, { useState, useEffect } from 'react';
import { ShoppingCart, Copy, Download, Plus, X, Check } from 'lucide-react';
import { apiClient } from '../utils/api';
import { getMealPlan } from '../api/meals';
import { toast } from 'react-hot-toast';
import { format, startOfWeek, endOfWeek } from 'date-fns';

interface ShoppingItem {
  id: string;
  item: string;
  quantity?: string;
  category: string;
  checked: boolean;
  mealId?: string;
  mealName?: string;
}

interface MealWithIngredients {
  id: string;
  name: string;
  emoji: string;
  date: string;
  ingredients?: string[];
}

export const ShoppingList: React.FC = () => {
  const [items, setItems] = useState<ShoppingItem[]>([]);
  const [weekMeals, setWeekMeals] = useState<MealWithIngredients[]>([]);
  const [newItem, setNewItem] = useState({ item: '', quantity: '', category: 'other' });
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState(new Date());

  useEffect(() => {
    loadShoppingList();
  }, []);

  const loadWeekMeals = async () => {
    try {
      const weekStart = startOfWeek(selectedWeek);
      const weekEnd = endOfWeek(selectedWeek);
      
      // Get meal plan for the week
      const response = await getMealPlan();
      if (response && response.slots) {
        const mealsWithIngredients = response.slots
          .filter((slot: any) => slot.selected_meal)
          .map((slot: any) => ({
            id: slot.selected_meal.id,
            name: slot.selected_meal.name,
            emoji: slot.selected_meal.emoji,
            date: slot.date,
            ingredients: slot.selected_meal.ingredients || []
          }));
        
        setWeekMeals(mealsWithIngredients);
        
        // Auto-generate shopping items from meal ingredients
        generateItemsFromMeals(mealsWithIngredients);
      }
    } catch (error) {
      console.error('Failed to load week meals:', error);
    }
  };

  const loadShoppingList = async () => {
    try {
      const response = await apiClient.getShoppingList();
      const lists = Array.isArray(response) ? response : [];
      if (lists.length > 0) {
        const active = lists[0];
        let rawItems = active.items;
        if (typeof rawItems === 'string') {
          try { rawItems = JSON.parse(rawItems); } catch { rawItems = []; }
        }
        // Flatten sections format to ShoppingItem[]
        if (Array.isArray(rawItems) && rawItems.length > 0 && rawItems[0]?.section) {
          const flat: ShoppingItem[] = rawItems.flatMap((s: any) =>
            (s.items || []).map((item: any) => ({
              id: item.name || Math.random().toString(),
              item: item.name,
              quantity: String(item.quantity || ''),
              category: s.section?.toLowerCase() || 'other',
              checked: item.checked || false,
            }))
          );
          setItems(flat);
        } else if (Array.isArray(rawItems)) {
          setItems(rawItems);
        }
      }
    } catch (error) {
      console.error('Failed to load shopping list:', error);
    }
    setLoading(false);
  };

  const generateItemsFromMeals = (meals: MealWithIngredients[]) => {
    const newItems: ShoppingItem[] = [];
    
    meals.forEach(meal => {
      if (meal.ingredients && meal.ingredients.length > 0) {
        meal.ingredients.forEach(ingredient => {
          // Parse ingredient (e.g., "2 cups flour" -> quantity: "2 cups", item: "flour")
          const match = ingredient.match(/^(\d+\s*\w*)\s+(.+)$/);
          const quantity = match ? match[1] : '';
          const item = match ? match[2] : ingredient;
          
          newItems.push({
            id: `${meal.id}-${item}`,
            item,
            quantity,
            category: categorizeIngredient(item),
            checked: false,
            mealId: meal.id,
            mealName: meal.name
          });
        });
      }
    });
    
    setItems(prevItems => {
      // Merge with existing items, avoiding duplicates
      const existingIds = new Set(prevItems.map(item => item.item.toLowerCase()));
      const uniqueNewItems = newItems.filter(item => 
        !existingIds.has(item.item.toLowerCase())
      );
      return [...prevItems, ...uniqueNewItems];
    });
  };

  const categorizeIngredient = (ingredient: string): string => {
    const categories: Record<string, string[]> = {
      produce: ['tomato', 'lettuce', 'onion', 'garlic', 'potato', 'carrot', 'apple', 'banana'],
      dairy: ['milk', 'cheese', 'yogurt', 'butter', 'cream'],
      meat: ['chicken', 'beef', 'pork', 'turkey', 'fish', 'salmon'],
      pantry: ['flour', 'sugar', 'salt', 'pepper', 'oil', 'pasta', 'rice'],
      frozen: ['frozen', 'ice cream'],
      bakery: ['bread', 'rolls', 'bagel'],
      other: []
    };
    
    const lowerIngredient = ingredient.toLowerCase();
    for (const [category, keywords] of Object.entries(categories)) {
      if (keywords.some(keyword => lowerIngredient.includes(keyword))) {
        return category;
      }
    }
    return 'other';
  };

  const handleAddItem = async () => {
    if (!newItem.item) return;

    try {
      await apiClient.addShoppingItem(newItem.item, newItem.quantity || undefined);
      setNewItem({ item: '', quantity: '', category: 'other' });
      await loadShoppingList();
    } catch (error) {
      // Fallback to local state if API fails
      const item: ShoppingItem = {
        id: Date.now().toString(),
        item: newItem.item,
        quantity: newItem.quantity,
        category: newItem.category,
        checked: false
      };
      setItems([...items, item]);
      setNewItem({ item: '', quantity: '', category: 'other' });
    }
  };

  const handleToggleItem = (id: string) => {
    setItems(items.map(item => 
      item.id === id ? { ...item, checked: !item.checked } : item
    ));
  };

  const handleDeleteItem = async (id: string) => {
    const updated = items.filter(item => item.id !== id);
    setItems(updated);
    // Save updated list to backend - convert to sections format with 'name' field
    try {
      const sections: Record<string, any[]> = {};
      updated.forEach(i => {
        const cat = (i.category || 'other').charAt(0).toUpperCase() + (i.category || 'other').slice(1);
        if (!sections[cat]) sections[cat] = [];
        sections[cat].push({ name: i.item, quantity: i.quantity || 1, checked: i.checked });
      });
      const sectionsArray = Object.entries(sections).map(([section, items]) => ({ section, items }));
      await apiClient.saveShoppingList(sectionsArray);
    } catch { }
  };

  const handleEditItem = (item: ShoppingItem) => {
    setNewItem({ item: item.item, quantity: item.quantity || '', category: item.category });
    handleDeleteItem(item.id);
  };

  const handleSaveList = async () => {
    try {
      await apiClient.saveShoppingList(items);
      toast.success('Shopping list saved');
    } catch (error) {
      toast.error('Failed to save shopping list');
    }
  };

  const copyToClipboard = () => {
    const text = generateListText();
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const downloadList = () => {
    const text = generateListText();
    const blob = new Blob([text], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `shopping-list-${format(selectedWeek, 'yyyy-MM-dd')}.txt`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const generateListText = () => {
    const grouped = groupItemsByCategory();
    let text = `Shopping List - Week of ${format(selectedWeek, 'MMM dd, yyyy')}\n\n`;
    
    Object.entries(grouped).forEach(([category, categoryItems]) => {
      text += `${category.toUpperCase()}\n`;
      categoryItems.forEach(item => {
        const checkbox = item.checked ? '✓' : '○';
        const quantity = item.quantity ? `${item.quantity} ` : '';
        const meal = item.mealName ? ` (for ${item.mealName})` : '';
        text += `${checkbox} ${quantity}${item.item}${meal}\n`;
      });
      text += '\n';
    });
    
    return text;
  };

  const groupItemsByCategory = () => {
    return items.reduce((grouped, item) => {
      const category = item.category || 'other';
      if (!grouped[category]) grouped[category] = [];
      grouped[category].push(item);
      return grouped;
    }, {} as Record<string, ShoppingItem[]>);
  };

  if (loading) {
    return <div className="p-4">Loading shopping list...</div>;
  }

  const groupedItems = groupItemsByCategory();

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <ShoppingCart className="w-5 h-5" />
          Shopping List
        </h3>
        <div className="flex gap-2">
          <button
            onClick={copyToClipboard}
            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
            title="Copy to clipboard"
          >
            <Copy className="w-4 h-4" />
            Copy
          </button>
          <button
            onClick={downloadList}
            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-1"
            title="Download as text file"
          >
            <Download className="w-4 h-4" />
            Download
          </button>
        </div>
      </div>

      {/* Meals for the week - hidden since we use shared list now */}
      {false && weekMeals.length > 0 && (
        <div className="bg-blue-50 p-3 rounded text-sm">
          <strong>Meals this week:</strong>
          <div className="mt-1 space-y-1">
            {weekMeals.map(meal => (
              <div key={meal.id}>
                {meal.emoji} {meal.name} - {format(new Date(meal.date + 'T12:00:00'), 'EEE')}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add new item */}
      <div className="flex gap-2">
        <input
          type="text"
          value={newItem.quantity}
          onChange={(e) => setNewItem({...newItem, quantity: e.target.value})}
          placeholder="Qty"
          className="w-20 p-2 border rounded"
        />
        <input
          type="text"
          value={newItem.item}
          onChange={(e) => setNewItem({...newItem, item: e.target.value})}
          onKeyPress={(e) => e.key === 'Enter' && handleAddItem()}
          placeholder="Add item..."
          className="flex-1 p-2 border rounded"
        />
        <select
          value={newItem.category}
          onChange={(e) => setNewItem({...newItem, category: e.target.value})}
          className="p-2 border rounded"
        >
          <option value="produce">Produce</option>
          <option value="dairy">Dairy</option>
          <option value="meat">Meat</option>
          <option value="pantry">Pantry</option>
          <option value="frozen">Frozen</option>
          <option value="bakery">Bakery</option>
          <option value="other">Other</option>
        </select>
        <button
          onClick={handleAddItem}
          className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Grouped items */}
      <div className="space-y-4">
        {Object.entries(groupedItems).map(([category, categoryItems]) => (
          <div key={category}>
            <h4 className="font-semibold text-sm text-gray-700 uppercase mb-2">
              {category}
            </h4>
            <div className="space-y-1">
              {categoryItems.map(item => (
                <div key={item.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded">
                  <button
                    onClick={() => handleToggleItem(item.id)}
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      item.checked 
                        ? 'bg-green-600 border-green-600' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {item.checked && <Check className="w-3 h-3 text-white" />}
                  </button>
                  <span className={`flex-1 ${item.checked ? 'line-through text-gray-400' : ''}`}>
                    {item.quantity && <span className="font-semibold">{item.quantity} </span>}
                    {item.item}
                    {item.mealName && <span className="text-xs text-gray-500 ml-2">(for {item.mealName})</span>}
                  </span>
                  <button
                    onClick={() => handleEditItem(item)}
                    className="px-2 py-1 text-blue-600 hover:bg-blue-50 rounded text-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteItem(item.id)}
                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Items auto-save per item - no manual save needed */}
    </div>
  );
};